import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, Users, Clock,
  ChevronRight, UserCheck, Calendar, Edit3, Eye,
  Mail, Building, Target, Award, BarChart, BookOpen as BookOpenIcon,
  CheckCircle2, XCircle, Plus, UserPlus, Trash2,
} from 'lucide-react';
import Modal from '../../components/common/Modal';
import MessageModal from '../../components/common/MessageModal';
import SidebarOverlay from '../../components/common/SidebarOverlay';
import Loading from '../../components/common/Loading';
import { useAppContext } from '../../context/AppContext';
import { useRoleData } from '../../hooks/useRoleData';
import { hasPermission, canPerformModuleAction, PERMISSIONS } from '../../utils/rbac';
import { modulesAPI, usersAPI } from '../../services/api';
import { formatDate } from '../../utils/dateFormatter';
import './ModuleDetails.css';
import { MODULE_CATEGORIES } from '../../constants/enums';

const ModuleDetails = () => {
  const { id } = useParams();
  const moduleId = parseInt(id, 10);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if user navigated from within the app (has location.state)
  const hasNavigation = !!location.state;
  
  // Smart back navigation: use browser history only if navigated from within app
  const handleBackNavigation = () => {
    navigate(-1);
  };
  
  const [activeTab, setActiveTab] = useState('content');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addContentModalOpen, setAddContentModalOpen] = useState(false);
  const [enrollTraineesModalOpen, setEnrollTraineesModalOpen] = useState(false);
  const [assignInstructorsModalOpen, setAssignInstructorsModalOpen] = useState(false);
  const [selectedContentIds, setSelectedContentIds] = useState([]);
  const [selectedTraineeIds, setSelectedTraineeIds] = useState([]);
  const [selectedInstructorIds, setSelectedInstructorIds] = useState([]);
  const [traineeSearch, setTraineeSearch] = useState('');
  const [instructorSearch, setInstructorSearch] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState('');
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    level: 'Beginner',
    status: 'Active',
  });
  const [moduleCategories, setModuleCategories] = useState(MODULE_CATEGORIES);
  const [userEditModalOpen, setUserEditModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [userEditForm, setUserEditForm] = useState(null);
  const [userModuleModalOpen, setUserModuleModalOpen] = useState(false);
  const [userForModule, setUserForModule] = useState(null);
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [addingContent, setAddingContent] = useState(false);
  const [enrollingTrainees, setEnrollingTrainees] = useState(false);
  const [assigningInstructors, setAssigningInstructors] = useState(false);
  const [savingUserEdit, setSavingUserEdit] = useState(false);
  const [savingModuleAssignment, setSavingModuleAssignment] = useState(false);
  const [removeTraineeModalOpen, setRemoveTraineeModalOpen] = useState(false);
  const [traineeToRemove, setTraineeToRemove] = useState(null);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [removingTrainee, setRemovingTrainee] = useState(false);
  const [removeInstructorModalOpen, setRemoveInstructorModalOpen] = useState(false);
  const [instructorToRemove, setInstructorToRemove] = useState(null);
  const [confirmInstructorEmail, setConfirmInstructorEmail] = useState('');
  const [removingInstructor, setRemovingInstructor] = useState(false);

  const { currentUser, showNotification } = useAppContext();
  const { filteredModules, filteredContent, filteredUsers, refreshData, loading, error } = useRoleData();
  const canViewTraineeDetails = currentUser?.role === 'Administrator' || currentUser?.role === 'Instructor';

  const [users, setUsers] = useState(filteredUsers);
  useEffect(() => {
    setUsers(filteredUsers);
  }, [filteredUsers]);

  // Fetch module categories from API
  useEffect(() => {
    const fetchModuleCategories = async () => {
      try {
        const response = await modulesAPI.getCategories();
        const fetchedCategories = response.data.data;
        if (fetchedCategories && fetchedCategories.length > 0) {
          setModuleCategories(fetchedCategories);
        }
      } catch (error) {
        console.error('Failed to fetch module categories:', error);
        // Keep default categories
      }
    };
    fetchModuleCategories();
  }, []);

  // Sync selectedUser with fresh data from API when users array updates
  useEffect(() => {
    if (selectedUser) {
      const freshUser = users.find(u => u.id === selectedUser.id);
      if (freshUser) {
        setSelectedUser(freshUser);
      }
    }
  }, [users]);

  const canNavigateToTraineeDetails = (user) => {
    return canViewTraineeDetails && user?.role === 'Trainee' && Boolean(user?.username);
  };

  const handleOpenTraineeDetails = (user) => {
    if (!canNavigateToTraineeDetails(user)) return;
    handleCloseSidebar();
    navigate(`/analytics/trainee/${encodeURIComponent(user.username)}`);
  };

  if (loading) {
    return (
      <div className="empty-state">
        <Loading fullScreen={false} message="Loading module..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <h3>Error loading module</h3>
        <p>{error}</p>
        <button className="btn btn-outline" onClick={() => refreshData()}>
          Retry
        </button>
      </div>
    );
  }

  const module = filteredModules.find((m) => m.id === moduleId);

  if (!module) {
    return (
      <div className="empty-state">
        <h3>Module not found</h3>
        <button className="btn btn-outline" onClick={handleBackNavigation}>
          Back to Modules
        </button>
      </div>
    );
  }

  const formattedCreatedDate = formatDate(module.createdDate);
  const creator = users.find(u => u.id === module.createdBy || u.name === module.createdBy);
  const creatorName = module.createdByName || (creator ? (creator.firstName && creator.lastName ? `${creator.firstName} ${creator.lastName}` : creator.name) : module.createdBy);

  const moduleContent = filteredContent.filter((c) => module.contentIds.includes(c.id));
  const canEditModule = canPerformModuleAction(currentUser, module, 'edit');
  const canEnroll = hasPermission(currentUser, PERMISSIONS.ENROLL_TRAINEES);
  const canAssignInstructors = hasPermission(currentUser, PERMISSIONS.MANAGE_ALL_USERS);
  const availableContent = filteredContent.filter(c => !module.contentIds.includes(c.id));
  const availableTrainees = users.filter(u => u.role === 'Trainee' && !module.traineeIds.includes(u.id));
  const filteredAvailableTrainees = availableTrainees.filter(t =>
    t.name.toLowerCase().includes(traineeSearch.toLowerCase()) ||
    t.email.toLowerCase().includes(traineeSearch.toLowerCase()) ||
    t.department.toLowerCase().includes(traineeSearch.toLowerCase())
  );
  const availableInstructors = users.filter(u => u.role === 'Instructor' && !module.instructorIds.includes(u.id));
  const filteredAvailableInstructors = availableInstructors.filter(i =>
    i.name.toLowerCase().includes(instructorSearch.toLowerCase()) ||
    i.email.toLowerCase().includes(instructorSearch.toLowerCase()) ||
    i.department.toLowerCase().includes(instructorSearch.toLowerCase())
  );
  let trainees = users.filter((u) => module.traineeIds.includes(u.id));
  if (currentUser?.role === 'Trainee') {
    trainees = trainees.filter(u => u.id === currentUser.id);
  }
  const instructors = users.filter((u) => module.instructorIds.includes(u.id));

  const levelColor = { Beginner: 'badge-green', Intermediate: 'badge-yellow', Advanced: 'badge-blue' };

  // Define tabs based on user role
  const tabs = [
    { id: 'content', label: 'Content', count: moduleContent.length, icon: BookOpen },
    { id: 'trainees', label: 'Trainees', count: trainees.length, icon: Users },
    { id: 'instructors', label: 'Instructors', count: instructors.length, icon: UserCheck },
  ].filter(tab => !(currentUser?.role === 'Trainee' && tab.id === 'trainees'));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditOpen = () => {
    setForm({
      title: module.title,
      description: module.description,
      category: module.category,
      level: module.level,
      status: module.status,
    });
    setEditModalOpen(true);
  };

  const handleEmailClick = (email) => {
    setMessageRecipient(email);
    setMessageModalOpen(true);
  };

  const handleSendMessage = ({ recipientEmail, subject, message }) => {
    // In a real app, you would send the message via API
    showNotification(`Message sent to ${recipientEmail} successfully!`, 'success');
    setMessageModalOpen(false);
  };

  const handleOpenEditModal = (user) => {
    // Get fresh user data from the current users array to ensure we have the latest data
    const freshUser = users.find(u => u.id === user.id) || user;
    setUserToEdit(freshUser);
    const isTrainee = freshUser.role === 'Trainee';

    const formData = {
      ...freshUser,
      // Copy role-appropriate arrays
      ...(isTrainee ? {
        enrolledModules: freshUser.enrolledModules ? [...freshUser.enrolledModules] : [],
        completedModules: freshUser.completedModules ? [...freshUser.completedModules] : [],
        progress: freshUser.progress || 0,
      } : {
        assignedModules: freshUser.assignedModules ? [...freshUser.assignedModules] : [],
        specialization: freshUser.specialization || '',
      })
    };

    setUserEditForm(formData);
    setUserEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setUserEditModalOpen(false);
    setUserToEdit(null);
    setUserEditForm(null);
  };

  const handleEditFormChange = (e) => {
    const { name, value, type } = e.target;

    // Convert numeric fields
    let processedValue = value;
    if (name === 'progress') {
      const num = parseInt(value, 10);
      processedValue = isNaN(num) ? 0 : Math.min(100, Math.max(0, num));
    }

    setUserEditForm(prev => {
      const updated = { ...prev, [name]: processedValue };

      // Handle role change
      if (name === 'role' && prev.role !== processedValue) {
        // Clear role-specific fields when role changes
        if (processedValue === 'Trainee') {
          // Switching to Trainee: clear instructor fields
          delete updated.assignedModules;
          delete updated.specialization;
          // Initialize trainee fields if not present
          if (!updated.enrolledModules) updated.enrolledModules = [];
          if (!updated.completedModules) updated.completedModules = [];
          if (!updated.progress) updated.progress = 0;
        } else if (processedValue === 'Instructor') {
          // Switching to Instructor: clear trainee fields
          delete updated.enrolledModules;
          delete updated.completedModules;
          delete updated.progress;
          // Initialize instructor fields if not present
          if (!updated.assignedModules) updated.assignedModules = [];
          if (!updated.specialization) updated.specialization = '';
        }
      }

      return updated;
    });
  };

  const handleSaveUserEdit = async (e) => {
    e.preventDefault();
    if (!userEditForm?.name?.trim() || !userEditForm?.email?.trim() || !userEditForm?.department?.trim()) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userEditForm.email)) {
      showNotification('Please enter a valid email address', 'error');
      return;
    }

    // Clean the data before saving - remove role-inappropriate fields
    const isTrainee = userEditForm.role === 'Trainee';
    const cleanedData = { ...userEditForm };

    // Remove fields that don't belong to the current role
    if (isTrainee) {
      delete cleanedData.assignedModules;
      delete cleanedData.specialization;
      // Ensure trainee fields exist
      if (!cleanedData.enrolledModules) cleanedData.enrolledModules = [];
      if (!cleanedData.completedModules) cleanedData.completedModules = [];
      if (cleanedData.progress === undefined) cleanedData.progress = 0;
    } else {
      delete cleanedData.enrolledModules;
      delete cleanedData.completedModules;
      delete cleanedData.progress;
      // Ensure instructor fields exist
      if (!cleanedData.assignedModules) cleanedData.assignedModules = [];
      if (!cleanedData.specialization) cleanedData.specialization = '';
    }

    setSavingUserEdit(true);
    try {
      await usersAPI.updateUser(userToEdit.id, cleanedData);

      // Update local state after successful API call
      setUsers(prev => prev.map(user =>
        user.id === userToEdit.id ? { ...user, ...cleanedData } : user
      ));

      // Update selectedUser if it's the same user
      if (selectedUser && selectedUser.id === userToEdit.id) {
        setSelectedUser({ ...selectedUser, ...cleanedData });
      }

      setUserEditModalOpen(false);
      setUserToEdit(null);
      setUserEditForm(null);
      showNotification('Profile updated successfully', 'success');
      // Refresh user data
      refreshData();
    } catch (error) {
      console.error('Failed to update user:', error);
      showNotification(
        error.response?.data?.error || 'Failed to update profile. Please try again.',
        'error'
      );
    } finally {
      setSavingUserEdit(false);
    }
  };

  const handleOpenModuleModal = (user) => {
    // Get fresh user data from the current users array to ensure we have the latest data
    const freshUser = users.find(u => u.id === user.id) || user;
    setUserForModule(freshUser);
    // Determine modules user is already enrolled/assigned to
    const currentModules = freshUser.role === 'Trainee'
      ? (freshUser.enrolledModules || [])
      : (freshUser.assignedModules || []);
    // Filter modules that are NOT already assigned/enrolled
    const available = filteredModules.filter(m => !currentModules.includes(m.id));
    setSelectedModuleIds([]); // start with empty selection
    setUserModuleModalOpen(true);
  };

  const handleCloseModuleModal = () => {
    setUserModuleModalOpen(false);
    setUserForModule(null);
    setSelectedModuleIds([]);
  };

  const handleModuleSelection = (moduleId) => {
    setSelectedModuleIds(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleSaveModuleAssignment = async () => {
    if (!userForModule || selectedModuleIds.length === 0) {
      showNotification('Please select at least one module', 'error');
      return;
    }

    setSavingModuleAssignment(true);
    try {
      // For each selected module, call appropriate API based on user role
      for (const moduleId of selectedModuleIds) {
        if (userForModule.role === 'Trainee') {
          await usersAPI.enrollTrainee(userForModule.id, moduleId);
        } else {
          // Instructor assignment
          await modulesAPI.assignInstructor(moduleId, userForModule.id);
        }
      }

      // Update local state after successful API calls
      setUsers(prev => prev.map(user => {
        if (user.id !== userForModule.id) return user;

        const isTrainee = user.role === 'Trainee';
        const field = isTrainee ? 'enrolledModules' : 'assignedModules';
        const current = user[field] || [];
        // Add new module IDs, avoid duplicates
        const updated = [...current, ...selectedModuleIds.filter(id => !current.includes(id))];

        return {
          ...user,
          [field]: updated
        };
      }));

      // Update selectedUser if it's the same user
      if (selectedUser && selectedUser.id === userForModule.id) {
        const isTrainee = selectedUser.role === 'Trainee';
        const field = isTrainee ? 'enrolledModules' : 'assignedModules';
        const current = selectedUser[field] || [];
        const updated = [...current, ...selectedModuleIds.filter(id => !current.includes(id))];
        setSelectedUser({
          ...selectedUser,
          [field]: updated
        });
      }

      showNotification(
        `${userForModule.role === 'Trainee' ? 'Enrolled' : 'Assigned'} ${selectedModuleIds.length} module(s) successfully`,
        'success'
      );
      handleCloseModuleModal();
      // Refresh data
      refreshData();
    } catch (error) {
      console.error('Failed to assign module:', error);
      showNotification(
        error.response?.data?.error || 'Failed to assign module. Please try again.',
        'error'
      );
    } finally {
      setSavingModuleAssignment(false);
    }
  };

  const handleSaveEdit = async () => {
    // Basic validation
    if (!form.title.trim() || !form.description.trim()) {
      showNotification('Title and description are required', 'error');
      return;
    }

    setSavingEdit(true);
    try {
      await modulesAPI.updateModule(moduleId, {
        title: form.title,
        description: form.description,
        category: form.category || null,
        level: form.level,
        status: form.status,
      });
      showNotification(`Module "${form.title}" updated successfully!`, 'success');
      setEditModalOpen(false);
      // Refresh module data
      refreshData();
    } catch (error) {
      console.error('Failed to update module:', error);
      showNotification(
        error.response?.data?.error || 'Failed to update module. Please try again.',
        'error'
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAddContent = async () => {
    if (selectedContentIds.length === 0) {
      showNotification('Please select at least one content item', 'error');
      return;
    }

    setAddingContent(true);
    try {
      // Add each selected content to module
      for (const contentId of selectedContentIds) {
        await modulesAPI.addContentToModule(moduleId, contentId);
      }
      showNotification(`Added ${selectedContentIds.length} content items to module`, 'success');
      setSelectedContentIds([]);
      setAddContentModalOpen(false);
      // Refresh module data
      refreshData();
    } catch (error) {
      console.error('Failed to add content to module:', error);
      showNotification(
        error.response?.data?.error || 'Failed to add content to module. Please try again.',
        'error'
      );
    } finally {
      setAddingContent(false);
    }
  };

  const handleEnrollTrainees = async () => {
    if (selectedTraineeIds.length === 0) {
      showNotification('Please select at least one trainee', 'error');
      return;
    }

    setEnrollingTrainees(true);
    try {
      // Enroll each selected trainee to module
      for (const traineeId of selectedTraineeIds) {
        await modulesAPI.enrollTrainee(moduleId, traineeId);
      }
      showNotification(`Enrolled ${selectedTraineeIds.length} trainees to module`, 'success');
      setSelectedTraineeIds([]);
      setEnrollTraineesModalOpen(false);
      // Refresh module data
      refreshData();
    } catch (error) {
      console.error('Failed to enroll trainees:', error);
      showNotification(
        error.response?.data?.error || 'Failed to enroll trainees. Please try again.',
        'error'
      );
    } finally {
      setEnrollingTrainees(false);
    }
  };

  const handleAssignInstructors = async () => {
    if (selectedInstructorIds.length === 0) {
      showNotification('Please select at least one instructor', 'error');
      return;
    }

    setAssigningInstructors(true);
    try {
      // Assign each selected instructor to module
      for (const instructorId of selectedInstructorIds) {
        await modulesAPI.assignInstructor(moduleId, instructorId);
      }
      showNotification(`Assigned ${selectedInstructorIds.length} instructors to module`, 'success');
      setSelectedInstructorIds([]);
      setAssignInstructorsModalOpen(false);
      // Refresh module data
      refreshData();
    } catch (error) {
      console.error('Failed to assign instructors:', error);
      showNotification(
        error.response?.data?.error || 'Failed to assign instructors. Please try again.',
        'error'
      );
    } finally {
      setAssigningInstructors(false);
    }
  };

  const handleOpenRemoveModal = (trainee) => {
    setTraineeToRemove(trainee);
    setConfirmEmail('');
    setRemoveTraineeModalOpen(true);
  };

  const handleRemoveTrainee = async () => {
    if (!traineeToRemove) return;

    // Validate email confirmation
    if (confirmEmail.trim() !== traineeToRemove.email) {
      showNotification('Please type the trainee email exactly to confirm removal', 'error');
      return;
    }

    if (!hasPermission(currentUser, PERMISSIONS.ENROLL_TRAINEES)) {
      showNotification('You do not have permission to remove trainees', 'error');
      return;
    }

    setRemovingTrainee(true);
    try {
      await modulesAPI.unenrollTrainee(moduleId, traineeToRemove.id);
      showNotification(`Trainee "${traineeToRemove.name}" removed from module successfully`, 'success');
      setRemoveTraineeModalOpen(false);
      setTraineeToRemove(null);
      setConfirmEmail('');
      // Refresh module data
      refreshData();
    } catch (error) {
      console.error('Failed to remove trainee:', error);
      showNotification(
        error.response?.data?.error || 'Failed to remove trainee. Please try again.',
        'error'
      );
    } finally {
      setRemovingTrainee(false);
    }
  };

  const handleOpenRemoveInstructorModal = (instructor) => {
    setInstructorToRemove(instructor);
    setConfirmInstructorEmail('');
    setRemoveInstructorModalOpen(true);
  };

  const handleRemoveInstructor = async () => {
    if (!instructorToRemove) return;

    // Validate email confirmation
    if (confirmInstructorEmail.trim() !== instructorToRemove.email) {
      showNotification('Please type the instructor email exactly to confirm removal', 'error');
      return;
    }

    // Ensure at least one instructor remains
    if (instructors.length <= 1) {
      showNotification('Cannot remove the last instructor from a module', 'error');
      return;
    }

    if (!hasPermission(currentUser, PERMISSIONS.MANAGE_ALL_USERS)) {
      showNotification('You do not have permission to remove instructors', 'error');
      return;
    }

    setRemovingInstructor(true);
    try {
      await modulesAPI.removeInstructor(moduleId, instructorToRemove.id);
      showNotification(`Instructor "${instructorToRemove.name}" removed from module successfully`, 'success');
      setRemoveInstructorModalOpen(false);
      setInstructorToRemove(null);
      setConfirmInstructorEmail('');
      // Refresh module data
      refreshData();
    } catch (error) {
      console.error('Failed to remove instructor:', error);
      showNotification(
        error.response?.data?.error || 'Failed to remove instructor. Please try again.',
        'error'
      );
    } finally {
      setRemovingInstructor(false);
    }
  };

  const handleViewDetails = (user) => {
    // Get fresh user data from the current users array to ensure we have the latest data
    const freshUser = users.find(u => u.id === user.id) || user;
    setSelectedUser(freshUser);
    setSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
    setSelectedUser(null);
  };

  const getModuleNames = (ids) =>
    ids
      ? ids
          .map((id) => filteredModules.find((m) => m.id === id)?.title)
          .filter(Boolean)
          .slice(0, 2)
      : [];

  const renderUserDetails = () => {
    if (!selectedUser) return null;
    const user = selectedUser;
    const isTrainee = user.role === 'Trainee';
    const isTraineeViewingInstructor = currentUser?.role === 'Trainee' && user.role === 'Instructor';
    const moduleIds = isTrainee ? user.enrolledModules : user.assignedModules;
    const moduleNames = getModuleNames(moduleIds);
    const completedCount = isTrainee ? (user.completedModules?.length || 0) : null;
    const totalCount = isTrainee ? (user.enrolledModules?.length || 0) : (user.assignedModules?.length || 0);

    return (
      <div className="user-details">
        {/* Header with avatar and basic info */}
        <div className="user-details__header">
          <div
            className="avatar user-details__avatar"
            style={{
              width: 72,
              height: 72,
              fontSize: '1.5rem',
              background:
                user.role === 'Instructor'
                  ? 'linear-gradient(135deg, var(--color-blue), #1e40af)'
                  : 'linear-gradient(135deg, var(--color-lime), var(--color-medium-green))',
            }}
          >
            {user.avatar ? (
              <img src={user.avatar} alt={user.name} />
            ) : (
              user.initials
            )}
          </div>
          <div className="user-details__header-info">
            <h2 className="user-details__name">{user.name}</h2>
            <div className="user-details__meta">
              <span className={`badge ${user.role === 'Instructor' ? 'badge-blue' : 'badge-green'}`}>
                {user.role}
              </span>
              <span className={`badge ${user.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                {user.status}
              </span>
              <span className="user-details__token">ID: {user.id}</span>
            </div>
            <p className="user-details__department">{user.department}</p>
          </div>
        </div>

        {/* Details grid */}
        <div className="user-details__grid">
          <div className="user-details__item">
            <Mail size={14} className="user-details__icon" />
            <div>
              <span className="user-details__label">Email</span>
              <button
                className="user-details__value link"
                onClick={() => handleEmailClick(user.email)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
              >
                {user.email}
              </button>
            </div>
          </div>
          <div className="user-details__item">
            <Calendar size={14} className="user-details__icon" />
            <div>
              <span className="user-details__label">Joined</span>
              <span className="user-details__value">{formatDate(user.joinDate)}</span>
            </div>
          </div>
          {!isTraineeViewingInstructor && (
            <div className="user-details__item">
              <Clock size={14} className="user-details__icon" />
              <div>
                <span className="user-details__label">Last Active</span>
                <span className="user-details__value">{formatDate(user.lastActive)}</span>
              </div>
            </div>
          )}
          {!isTraineeViewingInstructor && currentUser?.role !== 'Instructor' && currentUser?.role !== 'Administrator' && (
            <div className="user-details__item">
              <Target size={14} className="user-details__icon" />
              <div>
                <span className="user-details__label">Access Token</span>
                <span className="user-details__value user-details__token-code">{user.token}</span>
              </div>
            </div>
          )}
          {user.role === 'Instructor' && user.specialization && (
            <div className="user-details__item">
              <Award size={14} className="user-details__icon" />
              <div>
                <span className="user-details__label">Specialization</span>
                <span className="user-details__value">{user.specialization}</span>
              </div>
            </div>
          )}
        </div>

        {/* Progress for trainees */}
        {isTrainee && user.progress !== undefined && (
          <div className="user-details__section">
            <h3 className="user-details__section-title">
              <BarChart size={16} />
              Overall Progress
            </h3>
            <div className="user-details__progress">
              <div className="user-details__progress-header">
                <span>Completion Rate</span>
                <span className="user-details__progress-pct">{user.progress}%</span>
              </div>
              <div className="progress-bar">
                <div className="progress-bar-fill" style={{ width: `${user.progress}%` }} />
              </div>
              <div className="user-details__progress-stats">
                <span>Completed: {completedCount} of {totalCount} modules</span>
              </div>
            </div>
          </div>
        )}

        {/* Modules */}
        {!isTraineeViewingInstructor && (
          <div className="user-details__section">
            <h3 className="user-details__section-title">
              <BookOpenIcon size={16} />
              {isTrainee ? 'Enrolled Modules' : 'Assigned Modules'}
            </h3>
            {moduleIds && moduleIds.length > 0 ? (
              <div className="user-details__modules">
                {moduleNames.map((name, idx) => (
                  <div key={idx} className="user-details__module">
                    <ChevronRight size={12} className="user-details__module-icon" />
                    <span className="user-details__module-name">{name}</span>
                    {isTrainee && user.completedModules?.includes(moduleIds[idx]) && (
                      <span className="badge badge-green badge-sm">Completed</span>
                    )}
                  </div>
                ))}
                {moduleIds.length > moduleNames.length && (
                  <div className="user-details__module-more">
                    +{moduleIds.length - moduleNames.length} more modules
                  </div>
                )}
              </div>
            ) : (
              <p className="user-details__empty">No modules assigned yet.</p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="user-details__actions">
          {canNavigateToTraineeDetails(user) && (
            <button className="btn btn-outline btn-sm" onClick={() => handleOpenTraineeDetails(user)}>
              View Full Trainee Details
            </button>
          )}
          {/* Deactivate/Activate button - only for Admin */}
          {currentUser?.role === 'Administrator' && (
            <button className="btn btn-outline btn-sm">
              {user.status === 'Active' ? 'Deactivate' : 'Activate'}
            </button>
          )}
          {/* Enroll/Assign Module button - only for Admin/Instructor with permission */}
          {canEnroll && (
            isTrainee
              ? (currentUser?.role === 'Administrator' || currentUser?.role === 'Instructor') && (
                  <button className="btn btn-primary btn-sm" onClick={() => handleOpenModuleModal(user)}>
                    Enroll Module
                  </button>
                )
              : currentUser?.role === 'Administrator' && (
                  <button className="btn btn-primary btn-sm" onClick={() => handleOpenModuleModal(user)}>
                    Assign Module
                  </button>
                )
          )}
          {/* Edit Profile button - only for Admin */}
          {currentUser?.role === 'Administrator' && (
            <button className="btn btn-ghost btn-sm" onClick={() => handleOpenEditModal(user)}>
              Edit Profile
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="module-details">
      {/* Back Navigation */}
      <div className="module-details__back-nav">
        {hasNavigation && (
          <>
            <button className="btn btn-ghost module-details__back" onClick={handleBackNavigation} title="Go back to previous page">
              <ArrowLeft size={16} />
              Back
            </button>
            <span className="module-details__back-separator">|</span>
          </>
        )}
        <button className="btn btn-link module-details__back-parent" onClick={() => navigate('/modules')} title="Back to Modules list">
          Modules
        </button>
      </div>

      {/* Hero */}
      <div className="module-details__hero card">
        <div className="module-details__hero-icon">
          <BookOpen size={32} />
        </div>
        <div className="module-details__hero-content">
          <div className="module-details__hero-badges">
            <span className={`badge ${levelColor[module.level] || 'badge-gray'}`}>{module.level}</span>
            <span className={`badge ${module.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
              {module.status}
            </span>
            <span className="badge badge-dark">{module.category}</span>
          </div>
          <h1 className="module-details__hero-title">{module.title}</h1>
          <p className="module-details__hero-desc">{module.description}</p>
          <div className="module-details__hero-meta">
            <span><Calendar size={14} /> Created {formattedCreatedDate}</span>
            <span><UserCheck size={14} /> By {creatorName}</span>
            <span><Clock size={14} /> {module.duration} min total</span>
          </div>
        </div>
        <div className="module-details__hero-stats">
          <div className="module-details__hero-stat">
            <span className="module-details__hero-stat-value">{module.contentIds.length}</span>
            <span className="module-details__hero-stat-label">Content Items</span>
          </div>
          <div className="module-details__hero-stat">
            <span className="module-details__hero-stat-value">{module.traineeIds.length}</span>
            <span className="module-details__hero-stat-label">Enrolled</span>
          </div>
          <div className="module-details__hero-stat">
            <span className="module-details__hero-stat-value" style={{ color: 'var(--color-medium-green)' }}>
              {module.completionRate}%
            </span>
            <span className="module-details__hero-stat-label">Completion</span>
          </div>
        </div>
        {currentUser?.role !== 'Trainee' && (
          <div className="module-details__hero-actions">
            <button className="btn btn-outline module-details__edit-btn" onClick={handleEditOpen}>
              <Edit3 size={14} /> Edit Module
            </button>
            {canEnroll && canEditModule && (
                <button className="btn btn-primary module-details__enroll-btn" onClick={() => setEnrollTraineesModalOpen(true)}>
                  <UserPlus size={14} /> Enroll Trainees
                </button>
            )}
            {canAssignInstructors && canEditModule && (
                <button className="btn btn-primary module-details__enroll-btn" onClick={() => setAssignInstructorsModalOpen(true)}>
                  <UserCheck size={14} /> Assign Instructors
                </button>
            )}
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="card module-details__progress-card">
        <div className="module-details__progress-header">
          <div>
            <h3>Overall Progress</h3>
            <p>{module.completionRate}% of enrolled trainees have completed this module</p>
          </div>
          <span className="module-details__progress-pct">{module.completionRate}%</span>
        </div>
        <div className="progress-bar" style={{ height: 10 }}>
          <div className="progress-bar-fill" style={{ width: `${module.completionRate}%` }} />
        </div>
      </div>

      {/* Tabs */}
      <div className="module-details__tabs">
        {tabs.map(({ id: tid, label, count, icon: Icon }) => (
          <button
            key={tid}
            className={`module-details__tab ${activeTab === tid ? 'active' : ''}`}
            onClick={() => setActiveTab(tid)}
          >
            <Icon size={15} />
            {label}
            <span className="module-details__tab-count">{count}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="module-details__tab-content animate-fade-in">
        {activeTab === 'content' && (
          <div className="card">
            <div className="card-header">
              <div>
                <h3>Module Content ({moduleContent.length} items)</h3>
                <p className="text-sm text-muted">Click on any content to view details</p>
              </div>
              {canEditModule && (
                <button className="btn btn-primary btn-sm" onClick={() => setAddContentModalOpen(true)}>
                  <Plus size={14} />
                  Add Content
                </button>
              )}
            </div>
            <div className="module-details__content-list">
              {moduleContent.map((c, idx) => (
                <div
                  key={c.id}
                  className="module-details__content-item"
                  onClick={() => navigate(`/content-library/${c.id}`, { state: { fromModule: true } })}
                >
                  <div className="module-details__content-num">{idx + 1}</div>
                  <div className="module-details__content-thumb">
                    <img src={c.thumbnail || `https://via.placeholder.com/44x44/003a0f/ffffff?text=${encodeURIComponent(c.category)}`} alt={c.title} />
                  </div>
                  <div className="module-details__content-info">
                    <span className="module-details__content-title">{c.title}</span>
                    <div className="module-details__content-subtitle">
                      {c.subtitle ? c.subtitle : (c.description.length > 80 ? c.description.substring(0, 80) + '...' : c.description)}
                    </div>
                    <div className="module-details__content-meta">
                      <span className="badge badge-green">{c.category}</span>
                      <span><Clock size={11} /> {c.duration} min</span>
                      <span>{c.platform}</span>
                      <span>{c.completionCount} completions</span>
                    </div>
                  </div>
                  <div className="module-details__content-status">
                    <span className={`badge ${c.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                      {c.status}
                    </span>
                    <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {currentUser?.role !== 'Trainee' && activeTab === 'trainees' && (
          <div className="card">
            <div className="card-header">
              <div>
                <h3>Enrolled Trainees ({trainees.length})</h3>
              </div>
              {canEnroll && canEditModule && (
                <button className="btn btn-primary btn-sm" onClick={() => setEnrollTraineesModalOpen(true)}>
                  <UserPlus size={14} />
                  Enroll Trainees
                </button>
              )}
            </div>
            <div className="module-details__user-list">
              {trainees.map((u) => (
                <div key={u.id} className="module-details__user-item">
                  <div className="avatar" style={{ width: 42, height: 42, fontSize: '0.8rem' }}>
                    {u.avatar ? (
                      <img src={u.avatar} alt={u.name} />
                    ) : (
                      u.initials
                    )}
                  </div>
                  <div className="module-details__user-info">
                    {canNavigateToTraineeDetails(u) ? (
                      <button
                        type="button"
                        className="btn-link module-details__user-name"
                        onClick={() => handleOpenTraineeDetails(u)}
                        style={{ padding: 0, border: 'none', background: 'none', textAlign: 'left', font: 'inherit', cursor: 'pointer' }}
                      >
                        {u.name}
                      </button>
                    ) : (
                      <span className="module-details__user-name">{u.name}</span>
                    )}
                    <span className="module-details__user-dept">{u.department}</span>
                  </div>
                  <div className="module-details__user-progress">
                    <div className="module-details__user-progress-bar">
                      <div className="progress-bar">
                        <div className="progress-bar-fill" style={{ width: `${u.progress}%` }} />
                      </div>
                    </div>
                    <span className="module-details__user-pct">{u.progress}%</span>
                  </div>
                  <div>
                    <span className={`badge ${u.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                      {u.status}
                    </span>
                  </div>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleViewDetails(u)}
                    style={{ padding: '4px', width: 32, height: 32 }}
                  >
                    <Eye size={14} />
                  </button>
                  {canEnroll && canEditModule && (
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleOpenRemoveModal(u)}
                      style={{ padding: '4px', width: 32, height: 32, color: '#dc2626' }}
                      title="Remove trainee from module"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'instructors' && (
          <div className="card">
            <div className="card-header">
              <div>
                <h3>Assigned Instructors ({instructors.length})</h3>
              </div>
              {canAssignInstructors && canEditModule && (
                <button className="btn btn-primary btn-sm" onClick={() => setAssignInstructorsModalOpen(true)}>
                  <UserCheck size={14} />
                  Assign Instructors
                </button>
              )}
            </div>
            <div className="module-details__user-list">
              {instructors.map((u) => (
                <div key={u.id} className="module-details__user-item">
                  <div className="avatar" style={{ width: 42, height: 42, fontSize: '0.8rem', background: 'linear-gradient(135deg, var(--color-blue) 0%, #1e40af 100%)' }}>
                    {u.avatar ? (
                      <img src={u.avatar} alt={u.name} />
                    ) : (
                      u.initials
                    )}
                  </div>
                  <div className="module-details__user-info">
                    <span className="module-details__user-name">{u.name}</span>
                    <span className="module-details__user-dept">{u.specialization}</span>
                  </div>
                  <div>
                    <span className="badge badge-blue">Instructor</span>
                  </div>
                  <span className={`badge ${u.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                    {u.status}
                  </span>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleViewDetails(u)}
                    style={{ padding: '4px', width: 32, height: 32 }}
                  >
                    <Eye size={14} />
                  </button>
                  {hasPermission(currentUser, PERMISSIONS.MANAGE_ALL_USERS) && instructors.length > 1 && (
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleOpenRemoveInstructorModal(u)}
                      style={{ padding: '4px', width: 32, height: 32, color: '#dc2626' }}
                      title="Remove instructor from module"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Edit Module Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit Module"
        size="md"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setEditModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSaveEdit} disabled={savingEdit}>
              <Edit3 size={14} /> {savingEdit ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div className="form-group">
            <label className="form-label">Module Title *</label>
            <input
              className="form-input"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="e.g., Advanced Safety Training"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Description *</label>
            <textarea
              className="form-textarea"
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Describe the training objectives..."
              rows={3}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-4)' }}>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-select" name="category" value={form.category} onChange={handleChange}>
                <option value="">Select category</option>
                {moduleCategories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Level</label>
              <select className="form-select" name="level" value={form.level} onChange={handleChange}>
                {['Beginner', 'Intermediate', 'Advanced'].map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" name="status" value={form.status} onChange={handleChange}>
                <option value="Active">Active</option>
                <option value="Draft">Draft</option>
              </select>
            </div>
          </div>
        </div>
      </Modal>

      {/* Add Content Modal */}
      <Modal
        isOpen={addContentModalOpen}
        onClose={() => setAddContentModalOpen(false)}
        title="Add Content to Module"
        size="lg"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setAddContentModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleAddContent} disabled={addingContent}>
              <Plus size={14} /> {addingContent ? 'Adding...' : 'Add Selected Content'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <p>Select content to add to this module:</p>
          {availableContent.length === 0 ? (
            <div className="empty-state">
              <p>No available content to add. All content is already in this module.</p>
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Available Content <span className="text-muted">({selectedContentIds.length} selected)</span></label>
              <div className="checkbox-list" style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--color-dark-green-light)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3)' }}>
                {availableContent.map(content => (
                  <label key={content.id} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={selectedContentIds.includes(content.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedContentIds([...selectedContentIds, content.id]);
                        } else {
                          setSelectedContentIds(selectedContentIds.filter(id => id !== content.id));
                        }
                      }}
                      style={{ marginTop: '3px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{content.title}</div>
                      {content.subtitle && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>{content.subtitle}</div>}
                      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', display: 'flex', gap: 'var(--space-3)', marginTop: '2px' }}>
                        <span>{content.category}</span>
                        <span>•</span>
                        <span>{content.duration} min</span>
                        <span>•</span>
                        <span>{content.platform}</span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <small className="text-muted">
                Select content items to add to this module
              </small>
            </div>
          )}
        </div>
      </Modal>

      {/* Enroll Trainees Modal */}
      <Modal
        isOpen={enrollTraineesModalOpen}
        onClose={() => setEnrollTraineesModalOpen(false)}
        title="Enroll Trainees to Module"
        size="lg"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setEnrollTraineesModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleEnrollTrainees} disabled={enrollingTrainees}>
              <UserPlus size={14} /> {enrollingTrainees ? 'Enrolling...' : 'Enroll Selected Trainees'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <p>Select trainees to enroll in this module:</p>

          {/* Selected trainees list */}
          {selectedTraineeIds.length > 0 && (
            <div className="form-group">
              <label className="form-label">Selected Trainees ({selectedTraineeIds.length})</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', padding: 'var(--space-3)', border: '1px solid var(--color-dark-green-light)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)' }}>
                {availableTrainees
                  .filter(trainee => selectedTraineeIds.includes(trainee.id))
                  .map(trainee => (
                    <div key={trainee.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', background: 'var(--color-dark-green-light)', padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)' }}>
                      <span style={{ fontWeight: 500 }}>{trainee.name}</span>
                      <button
                        type="button"
                        onClick={() => setSelectedTraineeIds(selectedTraineeIds.filter(id => id !== trainee.id))}
                        style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 0, fontSize: '1.2rem' }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Search input */}
          <div className="form-group">
            <label className="form-label">Search Trainees</label>
            <input
              type="text"
              className="form-input"
              placeholder="Search by name, email, or department..."
              value={traineeSearch}
              onChange={(e) => setTraineeSearch(e.target.value)}
            />
          </div>

          {availableTrainees.length === 0 ? (
            <div className="empty-state">
              <p>No available trainees to enroll. All trainees are already enrolled in this module.</p>
            </div>
          ) : filteredAvailableTrainees.length === 0 ? (
            <div className="empty-state">
              <p>No trainees match your search.</p>
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Available Trainees <span className="text-muted">({selectedTraineeIds.length} selected)</span></label>
              <div className="checkbox-list" style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--color-dark-green-light)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3)' }}>
                {filteredAvailableTrainees.map(trainee => (
                  <label key={trainee.id} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={selectedTraineeIds.includes(trainee.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTraineeIds([...selectedTraineeIds, trainee.id]);
                        } else {
                          setSelectedTraineeIds(selectedTraineeIds.filter(id => id !== trainee.id));
                        }
                      }}
                      style={{ marginTop: '3px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{trainee.name}</div>
                      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', display: 'flex', gap: 'var(--space-3)', marginTop: '2px' }}>
                        <span>{trainee.department}</span>
                        <span>•</span>
                        <button
                          className="link"
                          onClick={() => handleEmailClick(trainee.email)}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
                        >
                          {trainee.email}
                        </button>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <small className="text-muted">
                Select trainees to enroll in this module
              </small>
            </div>
          )}
        </div>
      </Modal>

      {/* Assign Instructors Modal */}
      <Modal
        isOpen={assignInstructorsModalOpen}
        onClose={() => setAssignInstructorsModalOpen(false)}
        title="Assign Instructors to Module"
        size="lg"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setAssignInstructorsModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleAssignInstructors} disabled={assigningInstructors}>
              <UserCheck size={14} /> {assigningInstructors ? 'Assigning...' : 'Assign Selected Instructors'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <p>Select instructors to assign to this module:</p>

          {/* Selected instructors list */}
          {selectedInstructorIds.length > 0 && (
            <div className="form-group">
              <label className="form-label">Selected Instructors ({selectedInstructorIds.length})</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', padding: 'var(--space-3)', border: '1px solid var(--color-dark-green-light)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)' }}>
                {availableInstructors
                  .filter(instructor => selectedInstructorIds.includes(instructor.id))
                  .map(instructor => (
                    <div key={instructor.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', background: 'var(--color-dark-green-light)', padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)' }}>
                      <span style={{ fontWeight: 500 }}>{instructor.name}</span>
                      <button
                        type="button"
                        onClick={() => setSelectedInstructorIds(selectedInstructorIds.filter(id => id !== instructor.id))}
                        style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 0, fontSize: '1.2rem' }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Search input */}
          <div className="form-group">
            <label className="form-label">Search Instructors</label>
            <input
              type="text"
              className="form-input"
              placeholder="Search by name, email, or department..."
              value={instructorSearch}
              onChange={(e) => setInstructorSearch(e.target.value)}
            />
          </div>

          {availableInstructors.length === 0 ? (
            <div className="empty-state">
              <p>No available instructors to assign. All instructors are already assigned to this module.</p>
            </div>
          ) : filteredAvailableInstructors.length === 0 ? (
            <div className="empty-state">
              <p>No instructors match your search.</p>
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Available Instructors <span className="text-muted">({selectedInstructorIds.length} selected)</span></label>
              <div className="checkbox-list" style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--color-dark-green-light)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3)' }}>
                {filteredAvailableInstructors.map(instructor => (
                  <label key={instructor.id} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={selectedInstructorIds.includes(instructor.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedInstructorIds([...selectedInstructorIds, instructor.id]);
                        } else {
                          setSelectedInstructorIds(selectedInstructorIds.filter(id => id !== instructor.id));
                        }
                      }}
                      style={{ marginTop: '3px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{instructor.name}</div>
                      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', display: 'flex', gap: 'var(--space-3)', marginTop: '2px' }}>
                        <span>{instructor.department}</span>
                        <span>•</span>
                        <span>{instructor.specialization}</span>
                        <span>•</span>
                        <button
                          className="link"
                          onClick={() => handleEmailClick(instructor.email)}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
                        >
                          {instructor.email}
                        </button>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <small className="text-muted">
                Select instructors to assign to this module
              </small>
            </div>
          )}
        </div>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={userEditModalOpen}
        onClose={handleCloseEditModal}
        title={`Edit ${userToEdit?.role || 'User'} Profile`}
        size="md"
        footer={
          <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={handleCloseEditModal}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveUserEdit} disabled={savingUserEdit}>
              {savingUserEdit ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        }
      >
        {userEditForm && (
          <form onSubmit={handleSaveUserEdit}>
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input
                className="form-input"
                type="text"
                name="name"
                value={userEditForm.name || ''}
                onChange={handleEditFormChange}
                placeholder="Enter full name"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input
                className="form-input"
                type="email"
                name="email"
                value={userEditForm.email || ''}
                onChange={handleEditFormChange}
                placeholder="user@nhs.net"
                required
                readOnly={currentUser.role !== 'Administrator'}
                disabled={currentUser.role !== 'Administrator'}
                style={currentUser.role !== 'Administrator' ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
              />
              {currentUser.role !== 'Administrator' && (
                <p className="text-xs text-muted" style={{ marginTop: '4px' }}>
                  Only administrators can change email addresses
                </p>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Role *</label>
              <select
                className="form-select"
                name="role"
                value={userEditForm.role || 'Trainee'}
                onChange={handleEditFormChange}
                required
              >
                <option value="Trainee">Trainee</option>
                <option value="Instructor">Instructor</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Department *</label>
              <input
                className="form-input"
                type="text"
                name="department"
                value={userEditForm.department || ''}
                onChange={handleEditFormChange}
                placeholder="e.g., HSE Unit"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Status *</label>
              <select
                className="form-select"
                name="status"
                value={userEditForm.status || 'Active'}
                onChange={handleEditFormChange}
                required
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            {/* Role-specific fields will be added here */}
            {userEditForm.role === 'Trainee' && (
              <>
                <div className="form-group">
                  <label className="form-label">Progress (%) (auto-calculated)</label>
                  <input
                    className="form-input"
                    type="number"
                    name="progress"
                    min="0"
                    max="100"
                    value={userEditForm.progress || 0}
                    onChange={handleEditFormChange}
                    placeholder="0-100"
                    disabled
                    style={{ opacity: 0.7, cursor: 'not-allowed' }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Specialization (for Instructor)</label>
                  <input
                    className="form-input"
                    type="text"
                    name="specialization"
                    value={userEditForm.specialization || ''}
                    onChange={handleEditFormChange}
                    placeholder="e.g., Safety Procedures"
                    disabled
                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                  />
                  <small className="text-muted">This field is only for Instructors</small>
                </div>
              </>
            )}

            {userEditForm.role === 'Instructor' && (
              <>
                <div className="form-group">
                  <label className="form-label">Specialization</label>
                  <input
                    className="form-input"
                    type="text"
                    name="specialization"
                    value={userEditForm.specialization || ''}
                    onChange={handleEditFormChange}
                    placeholder="e.g., Safety Procedures"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Progress (%) (for Trainee)</label>
                  <input
                    className="form-input"
                    type="number"
                    name="progress"
                    min="0"
                    max="100"
                    value={userEditForm.progress || 0}
                    onChange={handleEditFormChange}
                    placeholder="0-100"
                    disabled
                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                  />
                  <small className="text-muted">This field is only for Trainees</small>
                </div>
              </>
            )}

          </form>
        )}
      </Modal>

      {/* Module Assignment Modal */}
      <Modal
        isOpen={userModuleModalOpen}
        onClose={handleCloseModuleModal}
        title={`${userForModule?.role === 'Trainee' ? 'Enroll' : 'Assign'} Module${selectedModuleIds.length > 1 ? 's' : ''}`}
        size="md"
        footer={
          <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={handleCloseModuleModal}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveModuleAssignment} disabled={savingModuleAssignment}>
              {savingModuleAssignment ? 'Processing...' : `${userForModule?.role === 'Trainee' ? 'Enroll' : 'Assign'} Selected`}
            </button>
          </div>
        }
      >
        {userForModule && (
          <div>
            <p className="text-muted" style={{ marginBottom: 'var(--space-4)' }}>
              Select module(s) to {userForModule.role === 'Trainee' ? 'enroll' : 'assign'} <strong>{userForModule.name}</strong> in.
            </p>
            <div className="modules-checkbox-group">
              {filteredModules.map(module => {
                const isAlready = userForModule.role === 'Trainee'
                  ? (userForModule.enrolledModules || []).includes(module.id)
                  : (userForModule.assignedModules || []).includes(module.id);
                if (isAlready) return null; // Skip already enrolled/assigned modules
                const isChecked = selectedModuleIds.includes(module.id);
                return (
                  <div key={module.id} className="module-checkbox">
                    <input
                      type="checkbox"
                      id={`module-assign-${module.id}`}
                      checked={isChecked}
                      onChange={() => handleModuleSelection(module.id)}
                    />
                    <label htmlFor={`module-assign-${module.id}`} className="module-checkbox-label">
                      {module.title}
                      <span className="module-checkbox-category">({module.category})</span>
                    </label>
                  </div>
                );
              })}
            </div>
            {selectedModuleIds.length === 0 && (
              <p className="text-muted" style={{ marginTop: 'var(--space-4)', fontStyle: 'italic' }}>
                No modules selected. Please select at least one module.
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* Remove Trainee Confirmation Modal */}
      <Modal
        isOpen={removeTraineeModalOpen}
        onClose={() => {
          setRemoveTraineeModalOpen(false);
          setTraineeToRemove(null);
          setConfirmEmail('');
        }}
        title="Remove Trainee from Module"
        size="md"
        footer={
          <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => {
              setRemoveTraineeModalOpen(false);
              setTraineeToRemove(null);
              setConfirmEmail('');
            }}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleRemoveTrainee} disabled={removingTrainee}>
              {removingTrainee ? 'Removing...' : 'Remove Trainee'}
            </button>
          </div>
        }
      >
        {traineeToRemove && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <p>
              You are about to remove <strong>{traineeToRemove.name}</strong> ({traineeToRemove.email}) from this module.
            </p>
            <p>
              This action will unenroll the trainee from the module and may affect their progress tracking.
            </p>
            <div className="form-group">
              <label className="form-label">
                Type the trainee's email <strong>{traineeToRemove.email}</strong> to confirm:
              </label>
              <input
                className="form-input"
                type="email"
                placeholder="Enter email address"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                autoFocus
              />
              <small className="text-muted">
                This extra step prevents accidental removal.
              </small>
            </div>
          </div>
        )}
      </Modal>

      {/* Remove Instructor Confirmation Modal */}
      <Modal
        isOpen={removeInstructorModalOpen}
        onClose={() => {
          setRemoveInstructorModalOpen(false);
          setInstructorToRemove(null);
          setConfirmInstructorEmail('');
        }}
        title="Remove Instructor from Module"
        size="md"
        footer={
          <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => {
              setRemoveInstructorModalOpen(false);
              setInstructorToRemove(null);
              setConfirmInstructorEmail('');
            }}>
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={handleRemoveInstructor}
              disabled={removingInstructor || instructors.length <= 1}
              title={instructors.length <= 1 ? 'Cannot remove the last instructor from a module' : ''}
            >
              {removingInstructor ? 'Removing...' : 'Remove Instructor'}
            </button>
          </div>
        }
      >
        {instructorToRemove && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <p>
              You are about to remove <strong>{instructorToRemove.name}</strong> ({instructorToRemove.email}) from this module.
            </p>
            <p>
              This action will unassign the instructor from the module. <strong>At least one instructor must remain assigned to the module.</strong>
            </p>
            {instructors.length <= 1 && (
              <div className="alert alert-warning">
                <strong>Warning:</strong> This is the only instructor assigned to this module. Removing them will leave the module without an instructor.
              </div>
            )}
            <div className="form-group">
              <label className="form-label">
                Type the instructor's email <strong>{instructorToRemove.email}</strong> to confirm:
              </label>
              <input
                className="form-input"
                type="email"
                placeholder="Enter email address"
                value={confirmInstructorEmail}
                onChange={(e) => setConfirmInstructorEmail(e.target.value)}
                autoFocus
              />
              <small className="text-muted">
                This extra step prevents accidental removal.
              </small>
            </div>
          </div>
        )}
      </Modal>

      {/* User Details Sidebar */}
      <SidebarOverlay
        isOpen={sidebarOpen}
        onClose={handleCloseSidebar}
        title={`${selectedUser?.role} Details`}
        width="520px"
      >
        {renderUserDetails()}
      </SidebarOverlay>
      <MessageModal
        isOpen={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        recipientEmail={messageRecipient}
        currentUserRole={currentUser?.role}
        onSend={handleSendMessage}
      />
    </div>
  );
};

export default ModuleDetails;
